import Styles from './schoolOffers.module.css';
import React, { useEffect, useState } from 'react';
import { doc, getDoc, getFirestore } from 'firebase/firestore';
import { SchoolOffer, saveSchoolOffers, schoolOffersOf } from '../../../0.functions/offers';

type Props = {
  uid: string;
  schoolId: string;
  user: any; // 学生のデータ(学校の画面で購読している最新のもの)
};

type SalonOption = { recruitId: string; companyId: string; name: string };

// サロン名の一覧(salonNameIndex/all、Cloud Functions の syncSalonNameIndex が searchIndex に合わせて更新)。
// 画面を開くたびに読み直さないよう、1回読んだら使い回す(約60KB)。
let salonListCache: Promise<SalonOption[]> | null = null;
const loadSalonList = (): Promise<SalonOption[]> => {
  if (!salonListCache) {
    salonListCache = getDoc(doc(getFirestore(), 'salonNameIndex', 'all'))
      .then((snap) => Object.entries((snap.data()?.salons ?? {}) as Record<string, { name?: string; companyId?: string }>)
        .map(([recruitId, v]) => ({ recruitId, companyId: v.companyId ?? '', name: v.name ?? '' }))
        .filter((o) => o.name && o.companyId))
      .catch((e) => { salonListCache = null; throw e; });
  }
  return salonListCache;
};

// 比べるときの形: 全角・半角をそろえ(NFKC)、小文字にし、空白を除く
const normalize = (text: string): string => text.normalize('NFKC').toLowerCase().replace(/\s+/g, '');

// 学生詳細の「内定承諾」一覧の下に出す、学校で内定を追加するフォーム。
// 学生がアプリで登録していない内定を、学校が学生から直接聞いて登録する。登録したものは上の一覧に
// 学生が登録したものと同じ形の行で「学校登録」の印付きで並び、そこから削除できる(userInfo/index.tsx)。
// 内定先はサロン名を入力する。入力中に memorii に求人があるサロンを候補に出し
// (大文字・小文字、全角・半角、空白の有無を区別せず、名前の途中でも一致するもの)、
// 候補から選んだときは求人(サロン)と紐づけて、サロン分析の内定承諾にも数える。
// 候補にないサロン(memorii を使っていないサロン)は名前だけで登録し、学生ごとの内定承諾にだけ数える。
const SchoolOffers: React.FC<Props> = ({ uid, schoolId, user }) => {
  const offers = schoolOffersOf(user, schoolId);

  const [ salonName, setSalonName ] = useState('');
  const [ linked, setLinked ] = useState<SalonOption | null>(null); // 候補から選んだサロン
  const [ suggestions, setSuggestions ] = useState<SalonOption[]>([]);
  const [ offeredAt, setOfferedAt ] = useState('');
  const [ memo, setMemo ] = useState('');
  const [ saving, setSaving ] = useState(false);
  const [ error, setError ] = useState('');

  // 候補にする memorii のサロンの一覧(開いたときに1回だけ読む)
  const [ salonList, setSalonList ] = useState<SalonOption[] | null>(null);
  const [ listError, setListError ] = useState(false);
  useEffect(() => {
    let cancelled = false;
    loadSalonList()
      .then((list) => { if (!cancelled) setSalonList(list); })
      .catch(() => { if (!cancelled) setListError(true); });
    return () => { cancelled = true; };
  }, []);

  // 入力に一致するサロン。名前の先頭が一致するものを先に、最大8件
  useEffect(() => {
    const q = normalize(salonName);
    if (!q || !salonList || linked?.name === salonName) { setSuggestions([]); return; }
    const matched = salonList
      .map((o) => ({ o, pos: normalize(o.name).indexOf(q) }))
      .filter((m) => m.pos >= 0)
      .sort((a, b) => a.pos - b.pos || a.o.name.length - b.o.name.length)
      .slice(0, 8)
      .map((m) => m.o);
    setSuggestions(matched);
  }, [salonName, linked, salonList]);

  // 入力したのに一覧をまだ読み込み中のときは「読み込み中」を出す
  const searching = !!salonName.trim() && salonList === null && !listError && linked?.name !== salonName;
  const noMatch = !!normalize(salonName) && salonList !== null && suggestions.length === 0 && linked?.name !== salonName;

  async function add() {
    setError('');
    const name = salonName.trim();
    if (!name) { setError('内定先のサロン名を入力してください。'); return; }
    // 候補から選んだあとに名前を書き換えた場合は紐づけない
    const option = linked && linked.name === name ? linked : null;
    const offer: SchoolOffer = {
      id: `${Date.now()}`,
      recruitId: option?.recruitId ?? '',
      companyId: option?.companyId ?? '',
      salonName: name,
      offeredAt,
      memo: memo.trim(),
      createdAt: Date.now(),
    };
    setSaving(true);
    try {
      await saveSchoolOffers(uid, schoolId, [...offers, offer]);
      setSalonName(''); setLinked(null); setSuggestions([]); setOfferedAt(''); setMemo('');
    } catch (e) {
      console.error('内定を登録できませんでした', e);
      setError('内定を登録できませんでした。時間をおいて、もう一度お試しください。');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className={Styles.section}>
      <p className={Styles.title}>学校で内定を追加</p>
      <p className={Styles.desc}>
        学生がアプリで登録していない内定を、学生から聞いて登録できます。登録した内定は上の一覧に「学校登録」として表示されます（学生・サロンには表示されません）。
        サロン名を入力すると memorii に求人があるサロンが候補に出ます。候補から選ぶとサロン分析の内定承諾にも数えます。
      </p>

      <div className={Styles.form}>
        <div className={Styles.salonField}>
          <input
            placeholder="内定先のサロン名"
            value={salonName}
            maxLength={40}
            onChange={(e) => { setSalonName(e.target.value); if (linked && e.target.value !== linked.name) setLinked(null); }}
            disabled={saving}
          />
          {searching && (
            <div className={Styles.suggestions}>
              <p className={Styles.loading}><span className={Styles.spinner} />サロンを検索しています…</p>
            </div>
          )}
          {noMatch && (
            <p className={Styles.noMatch}>memorii に一致するサロンはありません。この名前のまま登録できます（サロン分析には数えません）。</p>
          )}
          {listError && !!salonName.trim() && (
            <p className={Styles.noMatch}>候補を読み込めませんでした。サロン名をそのまま入力して登録できます。</p>
          )}
          {suggestions.length > 0 && (
            <div className={Styles.suggestions}>
              <p>memorii に求人があるサロン（選ぶとサロン分析にも反映されます）</p>
              {suggestions.map((o) => (
                <button key={o.recruitId} type="button" onClick={() => { setLinked(o); setSalonName(o.name); setSuggestions([]); }}>
                  {o.name}
                </button>
              ))}
            </div>
          )}
          {linked && linked.name === salonName && <span className={Styles.linkedNote}>memorii の求人と紐づけました</span>}
        </div>
        <input type="date" value={offeredAt} onChange={(e) => setOfferedAt(e.target.value)} disabled={saving} aria-label="内定日" />
        <input placeholder="メモ（任意）" value={memo} maxLength={100} onChange={(e) => setMemo(e.target.value)} disabled={saving} />
        <button type="button" className={Styles.primaryBtn} onClick={add} disabled={saving}>{saving ? '登録中...' : '内定を登録'}</button>
      </div>
      {error && <p className={Styles.error}>{error}</p>}
    </div>
  );
};

export default SchoolOffers;
